import DeleteIcon from '@mui/icons-material/Delete';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import {
	Grid,
	IconButton,
	List,
	ListItem,
	ListItemText,
	ListSubheader,
	Menu,
	MenuItem,
	Select,
	Tooltip,
} from '@mui/material';
import PropTypes from 'prop-types';
import { isEmpty, mergeAll } from 'ramda';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { CHECKPOINT_GROUP, ENTITIES, FILTER_VIOLATIONS, STREAM_TYPES, TABS, AUTHORIZED_ACTIONS } from '../consts';
import messages from '../intl/messages';
import { getUserId, getUserRole } from '../store/auth/selectors';
import { hideModal, showConfirmModal } from '../store/modal/actions';
import { deleteViolation, fetchViolations, setViolationReview, setViolationsReview } from '../store/model/actions';
import {
	getCurrentCheckpoints,
	getCurrentExam,
	getEvalsByTab,
	getNewViolations,
	getViolationsRoomScanNew,
} from '../store/model/selectors';
import { setVideoSeek } from '../store/ui/actions';
import { getTypeFilter } from '../store/ui/selectors';
import { msToTime } from '../utils/formatDate';
import negEvalsOnly from '../utils/negEvalsOnly';
import sortArrByObjProp from '../utils/sortArrByObjProp';
import checkUserAuthorization from '../utils/userRights';

const listStyles = makeStyles()((theme) => ({
	root: {
		width: '100%',
		overflow: 'auto',
		display: 'inline-block',
		padding: '0 !important',
		height: '498px',
		minHeight: '498px',
		'& .alignRight': {
			textAlign: 'right',
		},
	},
	listSection: {
		'& .commissionerTitle': { float: 'right', marginRight: '15px' },
		'& .reviewerTitle': { float: 'right', marginRight: '15px' },
	},
	ul: {
		backgroundColor: 'inherit',
		padding: 0,
		'& li': {
			margin: 0,
			padding: '0 20px',
			fontSize: '0.9rem',
			cursor: 'pointer',
			'& .MuiButtonBase-root': {
				margin: 0,
				padding: '10px',
			},
			'& .MuiButtonBase-root.delete-icon': {
				margin: 0,
				padding: 0,
			},
			'& .MuiTypography-root': {
				fontSize: '0.9rem',
			},
			'&.MuiListSubheader-sticky': {
				backgroundColor: theme.palette.primary.main,
				color: 'white',
			},
			'&.current': {
				backgroundColor: theme.palette.primary.light,
			},
			'&.fail': {
				color: theme.palette.error.main,
			},
			'& .fail': {
				color: theme.palette.error.main,
			},
			'&.susp': {
				color: theme.palette.warning.main,
			},
			'& .susp': {
				color: theme.palette.warning.main,
			},
			'&.pass': {
				color: theme.palette.success.main,
			},
			'& .pass': {
				color: theme.palette.success.main,
			},
			'& .MuiInputBase-root': {
				margin: '0 0 0 30px',
				width: '0%',
				'& .MuiSelect-root': { padding: '7px 14px' },
				'& svg': { display: 'none' },
			},
			'& .MuiListItemText-root': {
				flex: 'auto',
				margin: 0,
			},
			'& .commentContainer': {
				display: 'flex',
			},
			'& .comment': {
				textTransform: 'initial',
				textOverflow: 'ellipsis',
				overflow: 'hidden',
				whiteSpace: 'nowrap',
				maxWidth: '100%',
			},
			'& .MuiSelect-select.MuiSelect-select': {
				padding: 0,
				width: 0,
			},
		},
	},
}));

// Helpers to normalize time fields across violations
const getStartMs = (violation) => violation.startFromMs ?? violation.timeFromMs ?? violation.createdAt ?? 0;
const getEndMs = (violation) => violation.startToMs ?? violation.timeToMs ?? null;
const getDisplayTime = (violation) => {
	const start = getStartMs(violation);
	const end = getEndMs(violation);
	return end !== null && end !== undefined ? `${msToTime(start)} - ${msToTime(end)}` : msToTime(start);
};

export const getCurrentTab = (type) => {
	switch (type) {
		case STREAM_TYPES.IMAGE:
			return TABS.IDENTIFICATION;
		case STREAM_TYPES.SCREEN:
			return TABS.SCREEN;
		case STREAM_TYPES.MOBILE:
			return TABS.MOBILE;
		case STREAM_TYPES.WEBCAM:
			return TABS.CAMERA;
		default:
			return TABS.ROOM;
	}
};

export const filterByType = (violations, typeFilter, checkpoints = {}) => {
	const filtered = {};

	for (const [violation, details] of Object.entries(violations)) {
		filtered[violation] = Object.entries(details).filter(([, detail]) => {
			// Get checkpoint evaluationGroup if available
			const checkpoint = detail.checkpointId ? checkpoints[detail.checkpointId] : null;
			const evaluationGroup = checkpoint?.evaluationGroup;

			switch (typeFilter) {
				case STREAM_TYPES.SCREEN:
					return (
						detail.tab === TABS.SCREEN ||
						detail.streamType === STREAM_TYPES.SCREEN ||
						detail.streamType === STREAM_TYPES.SCREEN_CHUNKS ||
						detail.streamType === STREAM_TYPES.VIRTUALIZATION ||
						detail.streamType === STREAM_TYPES.HIDDEN_WINDOWS ||
						detail.streamType === STREAM_TYPES.PROCESS ||
						detail.checkpointGroup === CHECKPOINT_GROUP.PROCESS_ANALYSIS ||
						detail.checkpointGroup === CHECKPOINT_GROUP.WINDOW_LAYOUT ||
						detail.streamType === STREAM_TYPES.WINDOW_LAYOUT ||
						detail.streamType === 'displays'
					);
				case STREAM_TYPES.MOBILE:
					return detail.tab === TABS.MOBILE || detail.streamType === STREAM_TYPES.MOBILE;
				case STREAM_TYPES.IMAGE:
					return detail.tab === TABS.IDENTIFICATION || detail.streamType === STREAM_TYPES.IMAGE;
				case ENTITIES.VIDEO_ROOM:
					return (
						detail.tab === TABS.ROOM ||
						detail.checkpointGroup === CHECKPOINT_GROUP.ROOM_OBJECTS ||
						evaluationGroup === 'room'
					);
				default:
					return (
						(detail.streamType !== STREAM_TYPES.MOBILE &&
							detail.streamType !== STREAM_TYPES.PROCESS &&
							detail.tab === TABS.CAMERA &&
							detail.checkpointGroup !== CHECKPOINT_GROUP.ROOM_OBJECTS &&
							evaluationGroup !== 'room') ||
						((detail.streamType === STREAM_TYPES.WEBCAM ||
							detail.streamType === STREAM_TYPES.SOUND ||
							detail.streamType === STREAM_TYPES.HEADPOSE ||
							detail.streamType === STREAM_TYPES.WEBCAM_CHUNKS) &&
							detail.checkpointGroup !== CHECKPOINT_GROUP.ROOM_OBJECTS &&
							evaluationGroup !== 'room')
					);
			}
		});
	}

	return filtered;
};

const filterByUsersChoice = (userFilter, violations, violationIdInReview) => {
	const allViolations = mergeAll(Object.values(violations));

	switch (userFilter) {
		case FILTER_VIOLATIONS.ALL:
			return { [FILTER_VIOLATIONS.ALL]: mergeAll(Object.values(violations)) };

		case FILTER_VIOLATIONS.SUSP:
			for (const violation of Object.values(allViolations)) {
				if (violation.reviewerReview !== 'NOK' && violation.commissionerReview !== 'NOK') {
					delete allViolations[violation.id];
				}
			}
			return { [FILTER_VIOLATIONS.ALL]: allViolations };

		case FILTER_VIOLATIONS.NO_REVIEW:
			for (const violation of Object.values(allViolations)) {
				if (
					(violation.reviewerReview !== null || violation.commissionerReview !== null) &&
					violationIdInReview !== violation.id.toString()
				) {
					delete allViolations[violation.id];
				}
			}
			return { [FILTER_VIOLATIONS.ALL]: allViolations };

		case FILTER_VIOLATIONS.REGISTRY_DOMAINS:
			return {};

		default:
			return violations;
	}
};

const filterByRawFlag = (violations, rawFlag) => {
	const filtered = {};

	for (const [group, details] of Object.entries(violations)) {
		const entries = details.filter(([, detail]) => (rawFlag ? Boolean(detail.raw) : !Boolean(detail.raw)));
		if (entries.length) filtered[group] = entries;
	}

	return filtered;
};

const sortByTime = (unsorted) => {
	const sorted = {};

	for (const [group, violations] of Object.entries(unsorted)) {
		sorted[group] = violations.sort((a, b) => {
			const keyA = getStartMs(a[1]);
			const keyB = getStartMs(b[1]);

			if (keyA < keyB) return -1;
			if (keyA > keyB) return 1;
			return 0;
		});
	}

	return sorted;
};

const EllipsisWithTooltip = ({ text, className }) => {
	const ref = useRef(null);
	const [isOverflowed, setIsOverflowed] = useState(false);

	const checkOverflow = useCallback(() => {
		const el = ref.current;
		if (!el) return;
		setIsOverflowed(el.scrollWidth > el.clientWidth);
	}, []);

	useEffect(() => {
		checkOverflow();
	}, [checkOverflow, text]);

	useEffect(() => {
		window.addEventListener('resize', checkOverflow);
		return () => window.removeEventListener('resize', checkOverflow);
	}, [checkOverflow]);

	const content = (
		<div ref={ref} className={className}>
			{text}
		</div>
	);

	return isOverflowed ? (
		<Tooltip title={text} enterDelay={200}>
			{content}
		</Tooltip>
	) : (
		content
	);
};

const ViolationsList = ({ isAssignedToMe, userFilter, examId }) => {
	const { classes: classesList } = listStyles();
	const [filteredViolations, setFilteredViolations] = useState({});
	const [filteredRawViolations, setFilteredRawViolations] = useState({});
	const [currentViolation, setCurrentViolation] = useState(null);
	const [contextMenu, setContextMenu] = useState(null);
	const [violationIdInReview, setViolationIdInReview] = useState(null);
	const evalsByTab = useSelector(getEvalsByTab());
	const userRole = useSelector(getUserRole);
	const userId = useSelector(getUserId);
	const exam = useSelector(getCurrentExam);
	const violations = useSelector(getNewViolations);
	const violationsRoomScan = useSelector(getViolationsRoomScanNew);
	const dispatch = useDispatch();
	const checkpoints = useSelector(getCurrentCheckpoints());
	const canReviewAsReviewer = checkUserAuthorization(userRole, AUTHORIZED_ACTIONS.REVIEW_AS_REVIEWER);
	const typeFilter = useSelector(getTypeFilter);
	const isRoomTab = typeFilter === ENTITIES.VIDEO_ROOM;
	const ratingDetailOptions = sortArrByObjProp('code', negEvalsOnly(evalsByTab[getCurrentTab(typeFilter)]));

	// Fetch violations
	useEffect(() => {
		if (examId) dispatch(fetchViolations(examId));
	}, [examId, dispatch]);

	// Filtering
	useEffect(() => {
		const base = isRoomTab ? violationsRoomScan : violations;
		const processed = sortByTime(
			filterByType(filterByUsersChoice(userFilter, base, violationIdInReview), typeFilter, checkpoints)
		);
		setFilteredViolations(filterByRawFlag(processed, false));
		setFilteredRawViolations(filterByRawFlag(processed, true));
	}, [isRoomTab, typeFilter, userFilter, violationIdInReview, violations, violationsRoomScan, checkpoints]);

	const seekToNext = (index, violationId) => {
		const nextTimePointId = getStartMs(
			filteredViolations[userFilter === FILTER_VIOLATIONS.ALL ? FILTER_VIOLATIONS.ALL : violationId]?.[
				index + 1
			]?.[1] || {}
		);
		if (nextTimePointId) {
			setCurrentViolation(index + 1);
			dispatch(setVideoSeek(nextTimePointId + index));
		}
	};

	// Rating
	const handleRating = ({ index, rating, checkpointId, violationId, byViolation }) => {
		const evaluationTab = checkpoints[checkpointId].tab;
		const evaluationId = evalsByTab[evaluationTab].length ? evalsByTab[evaluationTab][0].id : '';

		if (rating === 'NOK') {
			// NOK - set violationId in review to open select w ratingDetailOptions
			setViolationIdInReview(violationId);
		} else if (byViolation) {
			// OK - bulk accept only not-yet-reviewed violations (for the active role).
			// Already reviewed (PASS/FAIL) must stay unchanged; pick the column the same way as the SET_VIOLATIONS_REVIEW reducer.
			const reviewField = checkUserAuthorization(userRole, AUTHORIZED_ACTIONS.REVIEW_AS_COMMISSIONER)
				? 'commissionerReview'
				: 'reviewerReview';
			const violationIds = Object.values(violations[byViolation.checkpointGroup] ?? {})
				.filter((_violation) => _violation[reviewField] == null)
				.map((_violation) => _violation.id);

			if (violationIds.length) {
				dispatch(setViolationsReview({ violationIds, review: 'OK', evaluationId }));
			}
		} else {
			// OK - review single violation
			dispatch(setViolationReview({ violationId, review: 'OK', evaluationId }));
			const nextTimePointId = getStartMs(
				filteredViolations[userFilter === FILTER_VIOLATIONS.ALL ? FILTER_VIOLATIONS.ALL : violationId]?.[
					index + 1
				]?.[1] || {}
			);
			if (nextTimePointId) {
				setCurrentViolation(index + 1);
				dispatch(setVideoSeek(nextTimePointId + index));
			}
		}
	};

	// Rating NOK detail
	const handleRatingDetail = ({ reviewerNote, evaluationId, violationId, index }) => {
		dispatch(setViolationReview({ reviewerNote, violationId, review: 'NOK', evaluationId }));
		seekToNext(index, violationId);
	};

	const handleCloseContextMenu = (pass) => {
		if (pass && contextMenu?.callback) contextMenu.callback();

		setContextMenu(null);
	};

	const handleOnContextMenu = (event, violation, checkpointGroup, index, violationId) => {
		event.preventDefault();
		setContextMenu(
			contextMenu === null
				? {
						mouseX: event.clientX - 2,
						mouseY: event.clientY - 4,
						callback: () =>
							handleRating({
								byViolation: violation,
								checkpointGroup,
								checkpointId: violation.checkpointId,
								index,
								rating: 'OK',
								violationId,
							}),
				  }
				: null
		);
	};

	const handleDeleteViolation = (id) => {
		dispatch(
			showConfirmModal({
				handler: () => {
					dispatch(deleteViolation(id));
					dispatch(hideModal());
				},
				text: <FormattedMessage {...messages.deleteViolationPrompt} />,
				title: <FormattedMessage {...messages.deleteViolation} />,
				button: <FormattedMessage {...messages.deleteViolation} />,
			})
		);
	};

	const forbiddenRegistersEntries = useMemo(() =>
		Object.entries(exam?.forbiddenRegisters ?? {}).flatMap(([regPath, values]) =>
			Object.entries(values).map(([key, value]) => [regPath, key, value])
		), [exam?.forbiddenRegisters]
	);
	const forbiddenDomainsEntries = useMemo(() =>
		Object.entries(exam?.forbiddenDomains ?? {}).filter(
			([, urls]) => Array.isArray(urls) && urls.length > 0
		), [exam?.forbiddenDomains]
	);
	const hasForbiddenFindings = forbiddenRegistersEntries.length > 0 || forbiddenDomainsEntries.length > 0;

	return (
		<List className={classesList.root}>
			{/* Review multiple violations as OK */}
			<Menu
				open={contextMenu !== null}
				onClose={() => handleCloseContextMenu(false)}
				anchorReference="anchorPosition"
				anchorPosition={contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
			>
				<MenuItem onClick={() => handleCloseContextMenu(true)}>
					<FormattedMessage {...messages.bulkReviewOk} />
				</MenuItem>
			</Menu>

			{/* Violations list */}
			{Object.entries(filteredViolations)
				.filter(([, details]) => !isEmpty(details))
				.map(([checkpointGroup, details]) => (
					<li key={`checkpointGroup-${checkpointGroup}`} className={classesList.listSection}>
						<ul className={classesList.ul}>
							<ListSubheader style={{ marginTop: 0, paddingTop: 0 }}>
								<Grid container spacing={0}>
									<Grid item xs={8}>
										<FormattedMessage {...messages.violation} />:{' '}
										<strong>
											{checkpointGroup === 'null' ? (
												<FormattedMessage {...messages.other} />
											) : checkpointGroup === FILTER_VIOLATIONS.ALL ? (
												<FormattedMessage {...messages.all} />
											) : checkpointGroup === CHECKPOINT_GROUP.WINDOW_LAYOUT ? (
												<FormattedMessage {...messages.checkpointGroupWindowLayout} />
											) : (
												checkpointGroup
											)}
										</strong>
									</Grid>
									<Grid item xs={2}>
										<div className="reviewerTitle">
											<FormattedMessage {...messages.reviewer} />
										</div>
									</Grid>
									<Grid item xs={2}>
										<div className="commissionerTitle">
											<FormattedMessage {...messages.commissioner} />
										</div>
									</Grid>
								</Grid>
							</ListSubheader>
							{Object.values(details).map(([violationId, violation], index) => {
								const reviewerOkClass = violation.reviewerReview === 'OK' ? 'pass' : '';
								const reviewerNokClass = violation.reviewerReview === 'NOK' ? 'susp' : '';
								const commissionerOkClass = violation.commissionerReview === 'OK' ? 'pass' : '';
								const commissionerNokClass = violation.commissionerReview === 'NOK' ? 'fail' : '';
								const violationRated = canReviewAsReviewer ? violation.reviewerReview : violation.commissionerReview;
								const isRatingOK = violationRated === 'OK';
								const rowStyle = !violationRated ? '' : isRatingOK ? 'pass' : canReviewAsReviewer ? 'susp' : 'fail';
								const isCurrent = currentViolation === index ? 'current' : '';

								return (
									<ListItem
										key={`item-${checkpointGroup}-${violationId}`}
										className={`${rowStyle} ${isCurrent}`}
										onClick={() => {
											setCurrentViolation(index);
											dispatch(setVideoSeek(getStartMs(violation) + index));
										}}
									>
										<Grid container spacing={0} direction="row" justifyContent="center" alignItems="center">
											<Grid item xs={8}>
												<Grid container spacing={0}>
													<Grid item xs={3}>
														<ListItemText primary={getDisplayTime(violation)} />
													</Grid>
													<Grid item xs={1}>
														{violation.isCustomViolation && (
															<Tooltip title={<FormattedMessage {...messages.deleteViolation} />}>
																<IconButton
																	disabled={violation.createdBy !== userId}
																	onClick={(e) => {
																		e.stopPropagation();
																		handleDeleteViolation(violation.id);
																	}}
																	className="delete-icon"
																>
																	<DeleteIcon />
																</IconButton>
															</Tooltip>
														)}
													</Grid>
													<Grid item xs={8} className="commentContainer">
														{!violation.reviewerNote ? (
															<EllipsisWithTooltip text={violation.comment} className="comment" />
														) : (
															<EllipsisWithTooltip text={violation.reviewerNote} className="comment red" />
														)}
														{violationId === violationIdInReview && (
															<Select
																onClose={() => setViolationIdInReview(null)}
																open
																variant="standard"
																onChange={(e) =>
																	handleRatingDetail({
																		reviewerNote: e.target.value.text,
																		evaluationId: e.target.value.id,
																		violationId,
																		index,
																	})
																}
															>
																{ratingDetailOptions.map(({ id, text }) => {
																	return (
																		<MenuItem key={`evalsByTab-${id}`} value={{ id, text }}>
																			{text}
																		</MenuItem>
																	);
																})}
															</Select>
														)}
													</Grid>
												</Grid>
											</Grid>
											<Grid item xs={2} className="alignRight" onClick={(e) => e.stopPropagation()}>
												<Tooltip title="Pass">
													<IconButton
														className={reviewerOkClass}
														disabled={!checkUserAuthorization(userRole, AUTHORIZED_ACTIONS.REVIEW_AS_REVIEWER)}
														onContextMenu={(event) =>
															handleOnContextMenu(event, violation, checkpointGroup, index, violationId)
														}
														onClick={() =>
															handleRating({
																checkpointGroup,
																index,
																rating: 'OK',
																checkpointId: violation.checkpointId,
																violationId,
															})
														}
														size="large"
													>
														<ThumbUpIcon />
													</IconButton>
												</Tooltip>

												<Tooltip title="Suspicious">
													<IconButton
														className={reviewerNokClass}
														disabled={!checkUserAuthorization(userRole, AUTHORIZED_ACTIONS.REVIEW_AS_REVIEWER)}
														onClick={() =>
															handleRating({
																checkpointGroup,
																index,
																rating: 'NOK',
																checkpointId: violation.checkpointId,
																violationId,
															})
														}
														size="large"
													>
														<ThumbDownIcon />
													</IconButton>
												</Tooltip>
											</Grid>
											<Grid item xs={2} className="alignRight" onClick={(e) => e.stopPropagation()}>
												<Tooltip title="Pass">
													<IconButton
														className={commissionerOkClass}
														disabled={
															!isAssignedToMe ||
															!checkUserAuthorization(userRole, AUTHORIZED_ACTIONS.REVIEW_AS_COMMISSIONER)
														}
														onContextMenu={(event) =>
															handleOnContextMenu(event, violation, checkpointGroup, index, violationId)
														}
														onClick={() =>
															handleRating({
																checkpointGroup,
																index,
																rating: 'OK',
																checkpointId: violation.checkpointId,
																violationId,
															})
														}
														size="large"
													>
														<ThumbUpIcon />
													</IconButton>
												</Tooltip>

												<Tooltip title="Fail">
													<IconButton
														className={commissionerNokClass}
														disabled={
															!isAssignedToMe ||
															!checkUserAuthorization(userRole, AUTHORIZED_ACTIONS.REVIEW_AS_COMMISSIONER)
														}
														onClick={() =>
															handleRating({
																checkpointGroup,
																index,
																rating: 'NOK',
																checkpointId: violation.checkpointId,
																violationId,
															})
														}
														size="large"
													>
														<ThumbDownIcon />
													</IconButton>
												</Tooltip>
											</Grid>
										</Grid>
									</ListItem>
								);
							})}
						</ul>
					</li>
				))}

			{/* Raw violations list (read-only) */}
			{!isEmpty(filteredRawViolations) &&
				(() => {
					const flatRaw = Object.values(filteredRawViolations).flat(); // array of [id, violation]
					const sortedRaw = flatRaw.sort((a, b) => getStartMs(a[1]) - getStartMs(b[1]));
					return (
						<li className={classesList.listSection}>
							<ul className={classesList.ul}>
								<ListSubheader style={{ marginBottom: 0, paddingBottom: 0 }}>
									<Grid container spacing={0}>
										<Grid item xs={8}>
											<FormattedMessage {...messages.allDetectedViolations} />
										</Grid>
									</Grid>
								</ListSubheader>
								{sortedRaw.map(([violationId, violation], index) => (
									<ListItem
										key={`raw-item-${violationId}`}
										onClick={() => {
											setCurrentViolation(index);
											dispatch(setVideoSeek(getStartMs(violation) + index));
										}}
									>
										<Grid container spacing={0} direction="row" justifyContent="center" alignItems="center">
											<Grid item xs={8} marginTop={1.4}>
												<Grid container spacing={0}>
													<Grid item xs={3}>
														<ListItemText primary={getDisplayTime(violation)} />
													</Grid>
													<Grid item xs={1} />
													<Grid item xs={8} className="commentContainer">
														{!violation.reviewerNote ? (
															<EllipsisWithTooltip text={violation.comment} className="comment" />
														) : (
															<EllipsisWithTooltip text={violation.reviewerNote} className="comment red" />
														)}
													</Grid>
												</Grid>
											</Grid>
											{/* no action columns for raw */}
											<Grid item xs={4} />
										</Grid>
									</ListItem>
								))}
							</ul>
						</li>
					);
				})()}

			{/* Forbidden registers and domains */}
			{hasForbiddenFindings && userFilter === FILTER_VIOLATIONS.REGISTRY_DOMAINS && (
				<>
					{forbiddenRegistersEntries.length > 0 && (
						<li className={classesList.listSection}>
							<ul className={classesList.ul}>
								<ListSubheader style={{ marginBottom: 0, paddingBottom: 0 }}>
									<Grid container spacing={0}>
										<Grid item xs={8}>
											<FormattedMessage {...messages.forbiddenRegisters} />
										</Grid>
									</Grid>
								</ListSubheader>
								{forbiddenRegistersEntries.map(([regPath, key, value]) => (
									<ListItem key={`reg-${regPath}-${key}`} style={{ cursor: 'default' }}>
										<Grid container spacing={0} direction="row" justifyContent="center" alignItems="center">
											<Grid item xs={8} marginY={1}>
												<ListItemText
													primary={`${regPath} \u2192 ${key}: ${value}`}
													style={{overflowWrap: 'break-word'}}
												/>
											</Grid>
											<Grid item xs={4} />
										</Grid>
									</ListItem>
								))}
							</ul>
						</li>
					)}
					{forbiddenDomainsEntries.length > 0 && (
						<li className={classesList.listSection}>
							<ul className={classesList.ul}>
								<ListSubheader style={{ marginBottom: 0, paddingBottom: 0 }}>
									<Grid container spacing={0}>
										<Grid item xs={8}>
											<FormattedMessage {...messages.forbiddenDomains} />
										</Grid>
									</Grid>
								</ListSubheader>
								{forbiddenDomainsEntries.map(([browser, urls]) => (
									<ListItem key={`dom-${browser}`} style={{ cursor: 'default' }}>
										<Grid container spacing={0} direction="row" justifyContent="center" alignItems="center">
											<Grid item xs={8} marginY={1}>
												<ListItemText
													primary={`${browser.charAt(0).toUpperCase() + browser.slice(1)}: ${urls.join(', ')}`}
													style={{overflowWrap: 'break-word'}}
												/>
											</Grid>
											<Grid item xs={4} />
										</Grid>
									</ListItem>
								))}
							</ul>
						</li>
					)}
				</>
			)}
		</List>
	);
};

ViolationsList.propTypes = {
	userFilter: PropTypes.string,
	isAssignedToMe: PropTypes.bool,
	examId: PropTypes.string,
};

export default ViolationsList;
